"use client"

import { useEffect, useState } from "react"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/auth/context"
import { Card, CardContent } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Calendar, MapPin, Search, ChevronRight, Waves, Star, Trophy, Info } from "lucide-react"
import Link from "next/link"
import { format } from "date-fns"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { triggerHaptic } from "@/hooks/use-platform"
import { LogSessionModal } from "@/components/log-session-modal"
import { Plus } from "lucide-react"

interface SurfSession {
  id: string
  session_date: string
  user_experience_rating: number | null
  conditions_rating: number | null
  notes: string | null
  surf_spots: {
    id: string
    name: string
    country: string | null
  }
  users: {
    id: string
    auth_id: string
    name: string
    email: string
    avatar_url: string | null
  }
}

export function MemberSessionsExplore() {
  const { user, loading: authLoading } = useAuth()
  const [sessions, setSessions] = useState<SurfSession[]>([])
  const [filteredSessions, setFilteredSessions] = useState<SurfSession[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [showLogModal, setShowLogModal] = useState(false)
  const [editingSession, setEditingSession] = useState<SurfSession | null>(null)
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  
  const supabase = createClient()

  const countries = Array.from(new Set(sessions.map(s => s.surf_spots.country).filter(Boolean))) as string[]

  useEffect(() => {
    if (authLoading) return

    const fetchSessions = async () => {
      setLoading(true)
      try {
        if (!user) {
          setSessions([])
          setFilteredSessions([])
          setLoading(false)
          return
        }

        // 1. Get internal user ID
        const { data: userData } = await supabase
          .from("users")
          .select("id")
          .eq("auth_id", user.id)
          .single()

        if (!userData) {
          setSessions([])
          setFilteredSessions([])
          setLoading(false)
          return
        }

        const internalUserId = userData.id
        
        // 2. Fetch sessions (using auth user ID as that's what's stored in surf_sessions)
        const { data: sessionsData, error: sessionsError } = await supabase
          .from("surf_sessions")
          .select(`
            id,
            session_date,
            user_experience_rating,
            conditions_rating,
            notes,
            surf_spots (id, name, country),
            users (id, auth_id, name, email, avatar_url)
          `)
          .eq("user_id", user.id)
          .order("session_date", { ascending: false })
          .limit(50)

        if (sessionsError) throw sessionsError

        setSessions(sessionsData as any || [])
        setFilteredSessions(sessionsData as any || [])

      } catch (error) {
        console.error("[MemberSessionsExplore] Error:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchSessions()
  }, [authLoading, user, supabase, refreshTrigger])

  useEffect(() => {
    const query = searchQuery.toLowerCase()
    const filtered = sessions.filter(session => {
      const matchesSearch = 
        session.users.name?.toLowerCase().includes(query) ||
        session.users.email.toLowerCase().includes(query) ||
        session.surf_spots.name.toLowerCase().includes(query) ||
        (session.notes?.toLowerCase().includes(query))
      
      const matchesCountry = !selectedCountry || session.surf_spots.country === selectedCountry
      
      return matchesSearch && matchesCountry
    })
    setFilteredSessions(filtered)
  }, [searchQuery, selectedCountry, sessions])

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Waves className="w-12 h-12 text-blue-500 animate-pulse mb-4" />
        <p className="text-slate-500 font-medium">Loading your sessions...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search your spots or notes..."
              className="pl-10 h-12 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2 self-end md:self-auto">
            <Badge variant="secondary" className="bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 px-3 py-1.5 rounded-xl border-0 text-xs font-bold">
              {filteredSessions.length} {filteredSessions.length === 1 ? 'Session' : 'Sessions'}
            </Badge>
            <Button 
              className="h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-bold shadow-lg shadow-blue-500/20"
              onClick={() => {
                triggerHaptic('impact')
                setShowLogModal(true)
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Session
            </Button>
          </div>
        </div>

        {/* Country Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCountry === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCountry(null)}
            className={`rounded-xl h-8 px-4 text-[10px] font-black uppercase tracking-wider ${
              selectedCountry === null 
                ? "bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900" 
                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            All Countries
          </Button>
          {countries.map((country) => (
            <Button
              key={country}
              variant={selectedCountry === country ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCountry(country)}
              className={`rounded-xl h-8 px-4 text-[10px] font-black uppercase tracking-wider ${
                selectedCountry === country
                  ? "bg-blue-500 text-white hover:bg-blue-600 border-0"
                  : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 hover:text-blue-500 hover:border-blue-200"
              }`}
            >
              {country}
            </Button>
          ))}
        </div>
      </div>

      {filteredSessions.length === 0 ? (
        <Card className="bg-white dark:bg-slate-900 border-dashed border-2 border-slate-200 dark:border-slate-800 rounded-[2.5rem] overflow-hidden">
          <CardContent className="p-20 text-center">
            <Waves className="w-16 h-16 text-slate-200 dark:text-slate-800 mx-auto mb-6" />
            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-50 mb-2">No sessions found</h3>
            <p className="text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
              {searchQuery ? "Try a different search term or clear the filter." : "You haven't logged any sessions yet."}
            </p>
            {searchQuery && (
              <Button 
                variant="link" 
                onClick={() => setSearchQuery("")}
                className="mt-4 text-blue-500 font-bold"
              >
                Clear search
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3 pb-20">
          {filteredSessions.map((session) => (
            <Link 
              key={session.id} 
              href={`/explore?spotId=${session.surf_spots.id}`}
              className="group"
            >
              <div className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-[1.5px] border-slate-100 dark:border-slate-800/50 rounded-3xl p-4 flex items-center gap-4 hover:border-blue-200 dark:hover:border-blue-900/30 transition-all shadow-sm active:scale-[0.98]">
                <Avatar className="h-12 w-12 border-2 border-white dark:border-slate-800 shadow-sm shrink-0">
                  <AvatarImage src={session.users.avatar_url || ""} alt={session.users.name} />
                  <AvatarFallback className="bg-blue-100 text-blue-600 font-black text-sm">
                    {session.users.name?.charAt(0) || session.users.email.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-black text-sm text-slate-900 dark:text-slate-100 truncate group-hover:text-blue-500 transition-colors">
                      {session.users.name || session.users.email.split('@')[0]}
                    </span>
                    <div className="flex items-center gap-1 text-slate-400 shrink-0">
                      <Calendar className="w-3 h-3" />
                      <span className="text-[10px] font-black uppercase tracking-wider">
                        {format(new Date(session.session_date), "MMM d")}
                      </span>
                    </div>
                    {new Date(session.session_date) > new Date() ? (
                      <Badge className="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 border-none text-[8px] font-black uppercase tracking-widest px-1.5 h-4">
                        Planned
                      </Badge>
                    ) : session.conditions_rating ? (
                      <Badge className="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 border-none text-[8px] font-black uppercase tracking-widest px-1.5 h-4">
                        Reviewed
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border-none text-[8px] font-black uppercase tracking-widest px-1.5 h-4">
                        Due for Review
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-blue-500" />
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 truncate">
                      {session.surf_spots.name}
                    </span>
                  </div>
                  
                  {session.notes && (
                    <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 truncate italic">
                      "{session.notes}"
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex flex-col items-end gap-1">
                    {session.user_experience_rating && (
                      <div className="flex items-center gap-0.5 text-amber-500">
                        <Star className="w-3 h-3 fill-current" />
                        <span className="text-xs font-black">{session.user_experience_rating}</span>
                      </div>
                    )}
                    {session.conditions_rating ? (
                      <div className="flex items-center gap-0.5 text-blue-500">
                        <Trophy className="w-3 h-3" />
                        <span className="text-xs font-black">{session.conditions_rating}</span>
                      </div>
                    ) : (
                      new Date(session.session_date) <= new Date() && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-7 px-2 bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg text-[10px] font-black uppercase tracking-wider"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setEditingSession(session)
                            setShowLogModal(true)
                          }}
                        >
                          Rate
                        </Button>
                      )
                    )}
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8 text-slate-300 hover:text-blue-500 transition-colors"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      setEditingSession(session)
                      setShowLogModal(true)
                    }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <LogSessionModal 
        open={showLogModal} 
        onOpenChange={(open) => {
          setShowLogModal(open)
          if (!open) setEditingSession(null)
        }}
        editingSession={editingSession}
        onSessionLogged={() => setRefreshTrigger(prev => prev + 1)}
      />
    </div>
  )
}
